import Announcer from "@/modules/Announcer";
import { updateCameraStationSupportedFeatures } from "@/modules/cloud/station";
import { useAuth } from "@/store/auth";
import { useStation } from "camera/store/station";
import { getIsPaired } from "camera/store/station/selectors";

type SupportedFeaturesKey = SupportedFeatures[number];

const supportedFeatures = {
  WEBRTC_PERFECT_NEGOTIATION: true,
  CAMERA_STATION_STOP_RECORDING: true,
  SINGLE_MOTION_DETECTION_ZONE: true,
  CAMERA_STATION_ALARM: true,
  CAMERA_STATION_FLASHLIGHT_NOT_SUPPORTED: true,
  CAMERA_STATION_LOW_LIGHT_NOT_SUPPORTED: true,
  CAMERA_STATION_VIDEO_RECORDING: true,
  CAMERA_STATION_INSTANT_VIDEO: true,
  WEBRTC_TRANSCEIVERS_V2: true
} as const;

export function getSupportedFeatures() {
  return Object.keys(supportedFeatures) as SupportedFeatures;
}

async function updateSupportedFeaturesIfNeeded() {
  const isPaired = getIsPaired(useStation.getState());
  if (!isPaired) return;

  // populates in deviceUpdater and is empty by default, so we can compare local and server
  const supportedFeaturesFromServer = useStation.getState().supportedFeatures;
  const xmppLogin = useAuth.getState().device.xmppCredentials?.xmppLogin;

  if (!xmppLogin) {
    log.err("No xmpp login found when trying to update supported features");
    return;
  }

  const foundSupportedFeatures = supportedFeaturesFromServer;
  let shouldUpdate = false;

  Object.keys(supportedFeatures).forEach((feature) => {
    if (!foundSupportedFeatures?.includes(feature as SupportedFeaturesKey)) {
      log.app("Feature support out of sync: ", feature);
      shouldUpdate = true;
    }
  });

  if (shouldUpdate) {
    log.app("Updating supported features");
    log.app("Received features: ", foundSupportedFeatures);
    log.app("Supported features: ", supportedFeatures);
    await updateCameraStationSupportedFeatures({
      xmppLogin,
      supportedFeatures: getSupportedFeatures()
    });
    useStation.getState().setSupportedFeatures(getSupportedFeatures());
    Announcer.getInstance().announceItemChange("DEVICES");
  } else {
    log.app("Supported features are up to date ", foundSupportedFeatures);
  }
}

export const supportedFeaturesUpdater = {
  updateIfNeeded: updateSupportedFeaturesIfNeeded
};
